import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { mediaEntity, type MediaDocType } from '@ast-data/shared';
import FullPageProgress from '../../components/FullPageProgress';
import { usePreferredApp } from '../../components/usePreferredApp';
import { useLiveDocs } from '../../db/useScopedDocs';
import {
    historyOwnedRoute,
    type HistoryRecordDoc
} from '../history/historyRecordPath';
import { useHistoryRecordPaths } from '../history/useHistoryRecordPaths';
import { mediaFocusPath } from './mediaDirectLink';

/**
 * `/media/<id>`: forwards to the description the picture belongs to, in the
 * app this user works in, with the picture marked (see mediaDirectLink).
 *
 * The lookup is the change log's — it knows how a Medium reaches its owner's
 * dialog — and stays live: on a device still replicating, the picture or its
 * description may arrive only after the link was opened, and then the page
 * forwards by itself.
 */
export default function MediaRedirect() {
    const { mediaId = '' } = useParams();
    const navigate = useNavigate();
    const area = usePreferredApp();
    const { results: media, loading } = useLiveDocs<MediaDocType>(
        mediaEntity.collection,
        { selector: { id: mediaId } }
    );
    const rows = useMemo(
        () => [{ entityType: mediaEntity.name, entityId: mediaId }],
        [mediaId]
    );
    const officePath = useHistoryRecordPaths(rows).get(mediaId);
    const target =
        area && officePath ? mediaFocusPath(officePath, area, mediaId) : null;

    useEffect(() => {
        if (target) {
            navigate(target, { replace: true });
        }
    }, [target, navigate]);

    const item = media[0];
    // Found, and in a description that shows pictures: only its owner is
    // still on its way.
    const ownerPending =
        item !== undefined &&
        historyOwnedRoute(mediaEntity.name)?.owner(item as HistoryRecordDoc) !==
            null;
    if (target || loading || !area || ownerPending) {
        return <FullPageProgress />;
    }
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                px: 2,
                textAlign: 'center'
            }}
        >
            <Typography sx={{ maxWidth: 480 }}>
                <FormattedMessage
                    id={
                        item
                            ? 'media.directLink.noPlace'
                            : 'media.directLink.notFound'
                    }
                />
            </Typography>
            <Button variant="outlined" component={RouterLink} to="/">
                <FormattedMessage id="notFound.toStart" />
            </Button>
        </Box>
    );
}
